import { type CSSProperties, useMemo, useState } from "react";
import { Button } from "@/ui";
import { EmptyState, SectionHead } from "@/features/shell/EmptyState";
import { type TimelineItem, useTimelineItems } from "./items";
import { TimelineScale } from "./TimelineScale";
import {
  ZOOMS,
  ZOOM_LABEL,
  type Zoom,
  itemsIn,
  rangeFor,
  shiftAnchor,
  titleFor,
} from "./timeScale";

const PREV = "‹";
const NEXT = "›";

const KIND_LABEL: Record<TimelineItem["kind"], string> = {
  task: "Planned",
  actual: "Actual",
  schedule: "Scheduled",
  event: "Event",
  reading: "Reading",
};

/**
 * The timeline: one set of dated items (planned tasks, events, metric readings) seen
 * at six zoom levels. Every scaled level shares one axis engine — only the window and
 * the slot size change — and the list level is the same items as rows.
 */
export function TimelinePage() {
  const [zoom, setZoom] = useState<Zoom>("month");
  const [anchor, setAnchor] = useState(() => new Date());

  // The window comes first: a Schedule has no dates of its own, so its occurrences
  // can only be expanded once we know what period is being looked at.
  const range = useMemo(() => rangeFor(zoom, anchor), [zoom, anchor]);
  const items = useTimelineItems(range);
  const visible = useMemo(() => itemsIn(items, range), [items, range]);

  if (items.length === 0) {
    return (
      <main className="console">
        <SectionHead title="Timeline" />
        <EmptyState
          title="Nothing dated yet"
          hint="The timeline draws planned tasks, events, and metric readings. Give a task a planned start and it will appear here."
        />
      </main>
    );
  }

  return (
    <main className="console">
      <div className="tl-controls">
        <div className="tl-nav">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label="Previous"
            onClick={() => setAnchor(shiftAnchor(zoom, anchor, -1))}
          >
            {PREV}
          </Button>
          <span className="tl-title">{titleFor(zoom, anchor)}</span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label="Next"
            onClick={() => setAnchor(shiftAnchor(zoom, anchor, 1))}
          >
            {NEXT}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setAnchor(new Date())}>
            Today
          </Button>
        </div>

        {/* Zoom is a set of exclusive options — a radio group, not five buttons. */}
        <div className="zoom" role="radiogroup" aria-label="Zoom">
          {ZOOMS.map((level) => (
            <button
              key={level}
              type="button"
              role="radio"
              aria-checked={zoom === level}
              className={zoom === level ? "zoom-option is-active" : "zoom-option"}
              onClick={() => setZoom(level)}
            >
              {ZOOM_LABEL[level]}
            </button>
          ))}
        </div>
      </div>

      <SectionHead title={ZOOM_LABEL[zoom]} count={visible.length} />
      {zoom === "list" ? (
        <ItemList items={visible} />
      ) : (
        <>
          <Legend />
          <TimelineScale zoom={zoom} range={range} items={visible} />
        </>
      )}
    </main>
  );
}

/** Four kinds of mark share one axis; the legend says which is which. */
function Legend() {
  return (
    <ul className="tl-legend">
      <li>
        <span className="tl-key is-schedule" /> Scheduled time
      </li>
      <li>
        <span className="tl-key is-planned" /> Planned task
      </li>
      <li>
        <span className="tl-key is-actual" /> Actual
      </li>
      <li>
        <span className="tl-key is-event" /> Event
      </li>
      <li>
        <span className="tl-key is-reading" /> Reading
      </li>
    </ul>
  );
}

/** The list zoom: the same items, one per row, in time order. */
function ItemList({ items }: { items: TimelineItem[] }) {
  if (items.length === 0) {
    return <EmptyState title="Nothing in this window" hint="Step to another period, or zoom out." />;
  }

  return (
    <ul className="row-list">
      {items.map((item) => (
        <li
          key={item.id}
          className="row"
          style={{ "--item-tint": item.colorHex ?? "var(--a8-border)" } as CSSProperties}
        >
          <span className="tl-dot" />
          <span className="row-title">{item.title}</span>
          <span className="tl-kind">{KIND_LABEL[item.kind]}</span>
          <span className="row-meta">{when(item)}</span>
        </li>
      ))}
    </ul>
  );
}

/** An instant reads as a moment; a span reads as a range. */
function when(item: TimelineItem): string {
  const start = new Date(item.startMs);
  if (item.startMs === item.endMs) return start.toLocaleString();
  return `${start.toLocaleDateString()} → ${new Date(item.endMs).toLocaleDateString()}`;
}
